"use client";

import { useEffect, useMemo, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useAuthGuard } from "@/hooks/useAuthGuard";

interface Product {
  id: number;
  product_name: string;
  category: string | null;
  spec: string | null;
  keyword_tags: unknown;
}

interface ManufacturerDetailOut {
  id: number;
  company: string;
  category: string;
  categories?: string[];
  url: string | null;
  region: string | null;
  premium_yn: boolean;
  product_count: number;
  category_counts?: Record<string, number>;
  summary?: string | null;
  products: Product[];
}

type PageError = "auth" | "tier" | "not_found" | "other" | null;

export default function ManufacturerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  useAuthGuard();    // hydration 후 비로그인 시 /login redirect
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  const [data, setData] = useState<ManufacturerDetailOut | null>(null);
  const [error, setError] = useState<PageError>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("");  // "" = 전체

  useEffect(() => {
    if (!hasHydrated) return;     // hydration 전엔 fetch 보류
    if (!isLoggedIn) {
      setError("auth");
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get<ManufacturerDetailOut>(`/api/manufacturers/${id}`, { headers })
      .then((r) => setData(r.data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) setError("auth");
        else if (status === 403) setError("tier");
        else if (status === 404) setError("not_found");
        else setError("other");
      })
      .finally(() => setLoading(false));
  }, [id, token, isLoggedIn, hasHydrated]);

  const filteredProducts = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    let list = data.products;
    if (activeCategory) {
      list = list.filter((p) =>
        activeCategory === "__none__"
          ? !p.category
          : (p.category ?? "") === activeCategory
      );
    }
    if (q) {
      list = list.filter(
        (p) =>
          p.product_name.toLowerCase().includes(q) ||
          (p.spec ?? "").toLowerCase().includes(q) ||
          (p.category ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, filter, activeCategory]);

  // 제품에 실제 등장하는 카테고리 + count (백엔드 category_counts 보다 신뢰도 ↑)
  const productCategories = useMemo(() => {
    if (!data) return { entries: [] as [string, number][], hasUncategorized: 0 };
    const counts: Record<string, number> = {};
    let uncat = 0;
    for (const p of data.products) {
      if (p.category) counts[p.category] = (counts[p.category] ?? 0) + 1;
      else uncat++;
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return { entries, hasUncategorized: uncat };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin text-4xl">🌀</div>
      </div>
    );
  }

  if (error === "auth") {
    return (
      <div className="px-4 py-12 text-center">
        <h1 className="text-lg font-bold text-text">🔒 로그인이 필요해요</h1>
        <Link href="/login" className="btn-primary text-sm mt-5 inline-block">로그인</Link>
      </div>
    );
  }
  if (error === "tier") {
    return (
      <div className="px-4 py-12 text-center">
        <h1 className="text-lg font-bold text-text">🔒 프로 플랜부터 볼 수 있어요</h1>
        <p className="text-sm text-text-sub mt-2">제품 리스트와 상세 정보는 프로 이상에서 제공해요</p>
        <Link href="/mypage/subscribe" className="btn-primary text-sm mt-5 inline-block">구독하기</Link>
        <button onClick={() => router.back()} className="block mx-auto mt-3 text-xs text-text-sub">
          돌아가기
        </button>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-text-sub">업체를 찾을 수 없어요.</p>
        <Link href="/manufacturers" className="mt-4 inline-block text-primary text-sm">
          ← 목록
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* 헤더 */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => router.back()} className="text-text-sub text-xl" aria-label="뒤로">
            ←
          </button>
          <h1 className="text-sm font-bold text-text truncate flex-1">{data.company}</h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* 업체 요약 카드 */}
        <section
          className={`card ${data.premium_yn ? "border-primary/40 bg-primary/5" : ""}`}
        >
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            {(data.categories && data.categories.length > 0 ? data.categories : [data.category]).map((c) => {
              const n = (data.category_counts ?? {})[c];
              return (
                <span
                  key={c}
                  className="text-2xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                >
                  {c}{typeof n === "number" && n > 0 ? ` ${n}` : ""}
                </span>
              );
            })}
            {data.premium_yn && (
              <span className="text-2xs font-bold text-primary">⭐ 프리미엄</span>
            )}
          </div>
          <h2 className="text-base font-bold text-text">{data.company}</h2>
          {data.summary && (
            <p className="mt-2 text-xs text-text-sub leading-relaxed">
              🤖 {data.summary}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-text-sub">
            {data.region && <span>📍 {data.region}</span>}
            <span>📦 제품 {data.product_count}개</span>
          </div>
          {data.url && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-bg-secondary hover:bg-bg-elevated text-sm font-semibold text-text transition-colors"
            >
              🔗 홈페이지에서 자세히 보기
            </a>
          )}
        </section>

        {/* 제품 리스트 */}
        <section className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-text">
              📦 제품 {data.product_count}개
            </h2>
            {data.products.length > 0 && (
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="제품 검색..."
                className="bg-bg-secondary text-xs px-2 py-1 rounded-lg border border-border outline-none focus:border-primary/50 max-w-32"
              />
            )}
          </div>

          {/* 카테고리 필터칩 — 2개 이상 카테고리가 있을 때만 노출 */}
          {data.products.length > 0 &&
            (productCategories.entries.length >= 2 ||
              (productCategories.entries.length >= 1 && productCategories.hasUncategorized > 0)) && (
            <div className="flex gap-1.5 flex-wrap mb-3">
              <button
                onClick={() => setActiveCategory("")}
                className={`text-2xs font-bold px-2.5 py-1 rounded-full transition-colors ${
                  activeCategory === ""
                    ? "bg-primary text-bg"
                    : "bg-bg-elevated text-text-sub hover:text-text"
                }`}
              >
                전체 {data.products.length}
              </button>
              {productCategories.entries.map(([cat, n]) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-2xs font-bold px-2.5 py-1 rounded-full transition-colors ${
                    activeCategory === cat
                      ? "bg-primary text-bg"
                      : "bg-bg-elevated text-text-sub hover:text-text"
                  }`}
                >
                  {cat} {n}
                </button>
              ))}
              {productCategories.hasUncategorized > 0 && (
                <button
                  onClick={() => setActiveCategory("__none__")}
                  className={`text-2xs font-bold px-2.5 py-1 rounded-full transition-colors ${
                    activeCategory === "__none__"
                      ? "bg-primary text-bg"
                      : "bg-bg-elevated text-text-sub hover:text-text"
                  }`}
                >
                  미분류 {productCategories.hasUncategorized}
                </button>
              )}
            </div>
          )}

          {data.products.length === 0 ? (
            <p className="text-xs text-text-sub text-center py-6">
              제품 데이터가 아직 수집되지 않았어요
            </p>
          ) : filteredProducts.length === 0 ? (
            <p className="text-xs text-text-sub text-center py-4">
              일치하는 제품이 없어요
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {filteredProducts.map((p) => (
                <li key={p.id} className="py-2.5 flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text truncate">{p.product_name}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-2xs text-text-sub">
                      {p.spec && <span>{p.spec}</span>}
                      {p.category && p.category !== data.category && (
                        <span>· {p.category}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="text-2xs text-text-sub mt-3 pt-2 border-t border-border">
            ⓘ 가격·재고·주문은 업체 홈페이지에서 확인해주세요
          </p>
        </section>
      </div>
    </div>
  );
}
